import { useMemo, useState } from 'react';
import { Icon } from '../../components/ui/Icon';
import { AdminStat } from './AdminStat';
import { AdminUsers } from './AdminUsers';
import { AdminRoles } from './AdminRoles';
import { AdminPerms } from './AdminPerms';
import { AdminAudit } from './AdminAudit';
import { UserDrawer } from './UserDrawer';
import { usePlatform } from '../../data/queries/usePlatform';
import type { User } from '../../data/types/platform';

type AdminTab = 'users' | 'roles' | 'perms' | 'audit';

export function AdminPanel() {
  const { data } = usePlatform();
  const [tab, setTab] = useState<AdminTab>('users');
  const [query, setQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const totals = useMemo(() => {
    const all = data?.users ?? [];
    return {
      total:    all.length,
      active:   all.filter((u) => u.status === 'active').length,
      pending:  all.filter((u) => u.status === 'pending').length,
      inactive: all.filter((u) => u.status === 'inactive').length,
      admins:   all.filter((u) => u.role === 'platform-admin' || u.role === 'head').length,
      mfa:      all.filter((u) => u.mfa).length,
    };
  }, [data?.users]);

  if (!data) return null;

  const tabs: Array<{ id: AdminTab; he: string; count: number | null; ic: string }> = [
    { id: 'users', he: 'משתמשים',     count: totals.total,        ic: 'users' },
    { id: 'roles', he: 'תפקידים',     count: data.roles.length,   ic: 'user-cog' },
    { id: 'perms', he: 'הרשאות',      count: null,                ic: 'key' },
    { id: 'audit', he: 'יומן פעולות', count: data.audit.length,   ic: 'history' },
  ];

  const pct = (num: number, den: number) => (den > 0 ? Math.round((num / den) * 100) : 0);

  return (
    <div className="admin">
      <div className="admin-head">
        <div className="admin-head-text">
          <div className="admin-eyebrow">
            <Icon name="shield" size={12} />
            <span>ניהול מערכת</span>
          </div>
          <h1>משתמשים והרשאות</h1>
          <p className="admin-sub">
            ניהול גישות לכל מערכות ביחד · {totals.total} משתמשים · {totals.admins} מנהלים · {totals.mfa} עם אימות דו-שלבי
          </p>
        </div>
        <div className="admin-head-actions">
          <button className="admin-btn ghost" type="button">
            <Icon name="download" size={14} />
            ייצוא רשימה
          </button>
          <button className="admin-btn primary" type="button">
            <Icon name="user-plus" size={14} />
            הוסף משתמש
          </button>
        </div>
      </div>

      <div className="admin-stats">
        <AdminStat label="סך משתמשים"     value={totals.total}     sub="כולל מוזמנים" tone="ink" />
        <AdminStat label="פעילים"          value={totals.active}    sub={`${pct(totals.active, totals.total)}% מהמשתמשים`} tone="positive" />
        <AdminStat label="ממתינים"         value={totals.pending}   sub="הזמנות פתוחות" tone="warning" />
        <AdminStat label="לא פעילים"       value={totals.inactive}  sub="לא נכנסו 7+ ימים" tone="dim" />
        <AdminStat label="מנהלים"          value={totals.admins}    sub="גישה מלאה למערכת" tone="ink" />
        <AdminStat
          label="אימות דו-שלבי"
          value={`${pct(totals.mfa, totals.total)}%`}
          sub={`${totals.mfa} / ${totals.total} משתמשים`}
          tone={totals.total > 0 && totals.mfa / totals.total > 0.7 ? 'positive' : 'warning'}
        />
      </div>

      <div className="admin-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            className={'admin-tab' + (tab === t.id ? ' active' : '')}
            onClick={() => setTab(t.id)}
            type="button"
          >
            <Icon name={t.ic} size={14} />
            {t.he}
            {t.count != null && <span className="admin-tab-count">{t.count}</span>}
          </button>
        ))}
      </div>

      <div className="admin-body">
        {tab === 'users' && (
          <AdminUsers
            users={data.users}
            roles={data.roles}
            perms={data.perms}
            suites={data.suites}
            query={query}
            setQuery={setQuery}
            onPick={setSelectedUser}
          />
        )}
        {tab === 'roles' && (
          <AdminRoles
            users={data.users}
            roles={data.roles}
            perms={data.perms}
            suites={data.suites}
          />
        )}
        {tab === 'perms' && (
          <AdminPerms roles={data.roles} perms={data.perms} suites={data.suites} />
        )}
        {tab === 'audit' && <AdminAudit audit={data.audit} />}
      </div>

      {selectedUser && (
        <UserDrawer
          user={selectedUser}
          roles={data.roles}
          perms={data.perms}
          suites={data.suites}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
}
